/**
 * One SOS voice call, from the operator's side.
 *
 * The sequence is three steps and the ORDER is the whole design. The helmet PULLS the operator's
 * audio rather than being sent it, so the operator must already be publishing before the device is
 * told to join — otherwise the device tries once, finds nothing, and gives up. So: prepare (the
 * broker opens the call and mints the room), publish the microphone, then start (the device is
 * handed a member list in which the operator is marked as live).
 *
 * Everything the operator learns after that arrives as a broker-synthesised `ack`. That matters
 * most for the outcome nobody wants to guess at: a firmware that never answers produces no error
 * anywhere, so without `unanswered` the call would ring for ever on a helmet that cannot take it.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { getValidAccessToken } from '../../../services/auth'
import { getLiveChannelUrl } from '../../../services/auth/config'
import { brokerHttpOrigin } from '../DashboardElements/helmet/helmet-stream-url'
import { useHelmetCommandSender, type HelmetAckPayload } from '../DashboardElements/helmet/useHelmetCommandSender'
import { captureMic, readPreferredGain, readPreferredMic, writePreferredGain, writePreferredMic } from './audio-inputs'
import {
  CHANNEL_GRACE_MS,
  PING_MS,
  PLAY_RETRY_MS,
  PLAY_WINDOW_MS,
  PREPARE_ACK_MS,
  releaseCall,
  type HelmetCallPhase,
} from './call-resources'
import { openMicPeer, openPlayPeer } from './helmet-call-peers'
import { CallGoneError, CallNotReadyError, callRtcUrl, newCallViewerId } from './helmet-call-signal'
import { clampGain, createMicChain, DEFAULT_GAIN, type MicChain } from './mic-chain'

export type { HelmetCallPhase } from './call-resources'

export interface UseHelmetCall {
  phase: HelmetCallPhase
  /** The wearer's audio + camera, once it arrives. */
  remoteStream: MediaStream | null
  micOn: boolean
  toggleMic: () => void
  /** MUST be called straight from a click — the microphone prompt needs the user gesture. */
  start: () => void
  hangUp: () => void
  /**
   * Whether the command channel is subscribed and `start()` can actually reach the helmet.
   *
   * Exposed because this hook's own socket is opened in an effect and is NEVER subscribed on the
   * first commit. A caller that starts itself on mount — which is exactly what the SOS panel does,
   * the operator having already pressed Accept — must wait for this instead of sampling it once,
   * or `start()` refuses on the spot and the wearer is never called.
   */
  connected: boolean
  /**
   * The operator's captured microphone, for measuring whether it is producing sound.
   *
   * A call can be connected, publishing opus and read by the helmet while this track is silent —
   * macOS hands the browser whatever its default input is, and WebRTC does not treat a dead input
   * as an error. Without this the panel has nothing to measure and the operator discovers it by
   * asking the wearer whether they can be heard.
   */
  micStream: MediaStream | null
  /** Move the live call to another input device, without re-negotiating anything. */
  switchMic: (deviceId: string) => Promise<void>
  /** How much the operator's microphone is boosted before it reaches the wearer. 1 is untouched. */
  micGain: number
  setMicGain: (value: number) => void
}

export function useHelmetCall(opts: { robotId: string | null; enabled?: boolean }): UseHelmetCall {
  const { robotId, enabled = true } = opts
  const viewerIdRef = useRef<string>(newCallViewerId())
  const [phase, setPhase] = useState<HelmetCallPhase>({ kind: 'idle' })
  // Read from inside the deadlines below. A timer closing over `phase` would need re-arming on
  // every transition, and re-arming a deadline is how a deadline stops being one.
  const phaseRef = useRef<HelmetCallPhase>({ kind: 'idle' })

  phaseRef.current = phase
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null)
  const [micOn, setMicOn] = useState(true)

  const micRef = useRef<MediaStream | null>(null)
  const [micStream, setMicStream] = useState<MediaStream | null>(null)
  // The gain stage between the microphone and the call. Held in a ref because changing the gain must
  // not re-render anything, and torn down with the call.
  const chainRef = useRef<MicChain | null>(null)
  const [micGain, setMicGainState] = useState<number>(() => readPreferredGain() ?? DEFAULT_GAIN)
  const gainRef = useRef(micGain)

  gainRef.current = micGain
  const pushRef = useRef<RTCPeerConnection | null>(null)
  const playRef = useRef<RTCPeerConnection | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([])
  // Read inside every async continuation, so one that resolves after the operator hung up cannot
  // attach itself to a call that no longer exists.
  const liveRef = useRef(false)
  /**
   * Whether `call_prepare` actually went out, i.e. whether the BROKER holds a call for us.
   *
   * Distinct from `liveRef`, which is set the moment the operator clicks — before the microphone
   * prompt has even been answered. That difference decides whether a failure owes the broker a
   * `call_stop`: a denied microphone stranded nothing and must ring nothing, while a failure after
   * prepare leaves a call standing that only the idle reaper would collect.
   */
  const preparedRef = useRef(false)

  const release = useCallback(() => {
    chainRef.current?.close()
    chainRef.current = null
    releaseCall({ mic: micRef, push: pushRef, play: playRef, abort: abortRef, timers: timersRef, live: liveRef })
    preparedRef.current = false
    setRemoteStream(null)
  }, [])

  const origin = useMemo(() => {
    const url = getLiveChannelUrl()

    return url ? brokerHttpOrigin(url) : null
  }, [])

  const fail = useCallback(
    (reason: string, detail?: string) => {
      // Tell the broker BEFORE releasing, and that order is the point: `releaseCall` clears
      // `liveRef` as its first statement, which also disarms the unmount backstop below — so once
      // release has run, nothing will ever send this.
      //
      // Without it, a failure reached AFTER `call_prepare` opened a broker-side call leaves that
      // call standing: the operator sees "the wearer's audio never arrived" and gives up, while the
      // helmet keeps publishing to a call only the idle reaper will collect. The operator cannot
      // even do it by hand — the panel shows Hang up only while the call is active and Dismiss only
      // once it is not, so after a failure there is no button that sends one.
      //
      // Safe on the paths where no call exists yet (a denied microphone, an unreachable channel):
      // the broker suppresses a `call_stop` for a session it holds no call on.
      if (preparedRef.current) senderRef.current?.({ kind: 'call_stop', viewerId: viewerIdRef.current })
      release()
      setPhase(detail ? { kind: 'failed', reason, detail } : { kind: 'failed', reason })
    },
    [release],
  )

  /**
   * The command sender, held in a ref rather than closed over.
   *
   * Not a style choice. Every effect and callback below would otherwise depend on the sender's
   * IDENTITY, and the teardown effect that hangs up on unmount would then run its cleanup on any
   * render that produced a new one — ending a live emergency call because a parent re-rendered.
   */
  const senderRef = useRef<((intent: Record<string, unknown>) => void) | null>(null)

  /** Offer the receive side, retrying while the media server is still assembling the feed. */
  const attemptPlay = useCallback(
    (deadline: number) => {
      if (!liveRef.current || !origin || !robotId) return

      void (async () => {
        try {
          const token = await getValidAccessToken()
          const pc = await openPlayPeer({
            url: callRtcUrl(origin, robotId, 'play', viewerIdRef.current),
            token,
            ...(abortRef.current ? { signal: abortRef.current.signal } : {}),
            onTrack: stream => {
              if (!liveRef.current) return
              setRemoteStream(stream)
              setPhase({ kind: 'connected' })
            },
            // A peer that dies after connecting ends the call rather than freezing on a still
            // frame — an operator must never believe they are still being heard.
            onDropped: () => {
              if (liveRef.current) fail('connection_lost')
            },
          })

          if (!liveRef.current) {
            pc.close()

            return
          }

          playRef.current = pc
        } catch (err) {
          if (!liveRef.current) return
          // "Not ready" is the expected answer for the first seconds: the device has been told to
          // publish and has not started. Anything else is real.
          if (err instanceof CallNotReadyError) {
            if (Date.now() < deadline) {
              timersRef.current.push(setTimeout(() => attemptPlay(deadline), PLAY_RETRY_MS))

              return
            }

            // The window closed and the wearer's feed never registered. Giving up SILENTLY here is
            // the worst option available: the helmet may well have answered, so the broker's own
            // probe is satisfied and tears nothing down, and the operator is left on "Ringing the
            // helmet…" indefinitely while somebody talks into a call nobody is receiving.
            return fail('media_timeout')
          }

          if (err instanceof CallGoneError) return fail('call_gone')
          fail('media_failed', err instanceof Error ? err.message : undefined)
        }
      })()
    },
    [origin, robotId, fail],
  )

  /** Publish the microphone, then — and only then — tell the helmet to join. */
  const publishThenStart = useCallback(() => {
    // The PROCESSED stream when a gain stage exists, so the operator's gain reaches the wearer.
    const stream = chainRef.current?.stream ?? micRef.current
    const track = stream?.getAudioTracks()[0]

    if (!stream || !track || !origin || !robotId) return fail('mic_unavailable')

    void (async () => {
      try {
        const token = await getValidAccessToken()
        const pc = await openMicPeer(track, stream, {
          url: callRtcUrl(origin, robotId, 'push', viewerIdRef.current),
          token,
          ...(abortRef.current ? { signal: abortRef.current.signal } : {}),
          onDropped: () => {
            if (liveRef.current) fail('connection_lost')
          },
        })

        if (!liveRef.current) {
          pc.close()

          return
        }

        pushRef.current = pc
        senderRef.current?.({ kind: 'call_start', viewerId: viewerIdRef.current })
        setPhase({ kind: 'ringing' })

        // A REAL timer, not only the deadline `attemptPlay` tests inside its own catch block. That
        // deadline is reached only by way of a REJECTED offer; a promise that hangs instead of
        // rejecting queues no retry and raises no failure, and the call rings for ever with the
        // microphone open.
        timersRef.current.push(
          setTimeout(() => {
            if (liveRef.current && phaseRef.current.kind === 'ringing') fail('media_timeout')
          }, PLAY_WINDOW_MS),
        )
        attemptPlay(Date.now() + PLAY_WINDOW_MS)
      } catch (err) {
        if (!liveRef.current) return
        fail('publish_failed', err instanceof Error ? err.message : undefined)
      }
    })()
  }, [origin, robotId, fail, attemptPlay])

  const onAck = useCallback(
    (p: HelmetAckPayload) => {
      if (p.kind !== 'call' || !liveRef.current) return

      if (p.state === 'prepared') return publishThenStart()
      // `joined` is the device's first sign of life. The feed still has to come up, so this does
      // not mean connected — but it does mean the helmet is answering, which is what the operator
      // is waiting to know.
      if (p.state === 'joined') return
      if (p.state === 'unanswered') {
        release()
        setPhase({ kind: 'failed', reason: 'unanswered' })

        return
      }

      if (p.state === 'ended') {
        release()
        setPhase({ kind: 'ended', reason: p.reason ?? null })

        return
      }

      if (p.state === 'failed') fail(p.reason ?? 'failed')
    },
    [publishThenStart, release, fail],
  )

  // A closed tab must never leave a wearer on an open call with nobody listening.
  //
  // REGISTERED BEFORE `useHelmetCommandSender`, and that position is load-bearing rather than
  // tidy. React runs a component's cleanups in the order its effects were created, and the sender
  // owns the WebSocket's lifecycle effect — so when this sat below it, the socket was ALREADY
  // closed by the time this ran and `call_stop` was dropped on the floor with no error. The
  // comment above promised something the code did not do: an operator logging out, or navigating
  // in a way that unmounts the overlay, left the wearer publishing to a call nobody was on until
  // the idle reaper collected it.
  //
  // EMPTY deps, deliberately, and `release` is reached through a ref for the same reason the sender
  // is: this cleanup means "the operator's window is gone". Any dependency here turns an ordinary
  // re-render into a hang-up.
  const releaseRef = useRef(release)
  releaseRef.current = release
  useEffect(
    () => () => {
      if (liveRef.current) senderRef.current?.({ kind: 'call_stop', viewerId: viewerIdRef.current })
      releaseRef.current()
    },
    [],
  )

  const { send, connected } = useHelmetCommandSender({
    robotId,
    enabled: enabled && Boolean(robotId),
    onOffline: () => {
      if (liveRef.current) fail('offline')
    },
    // Any OTHER refusal ends the call rather than leaving it mid-handshake. The one an operator
    // will actually meet is `forbidden_role`: a read-only account can subscribe but not command, so
    // Accept would otherwise hang on "Getting your microphone ready…" with nothing to explain it.
    onRejected: code => {
      if (!liveRef.current || code === 'robot_offline') return
      fail(code === 'forbidden_role' ? 'forbidden_role' : 'rejected', code)
    },
    onAck,
  })

  senderRef.current = send as unknown as (intent: Record<string, unknown>) => void

  const start = useCallback(() => {
    if (!robotId || !origin) return fail('calls_unavailable')
    if (phase.kind === 'starting' || phase.kind === 'ringing' || phase.kind === 'connected') return
    // Refuse BEFORE opening a microphone, because an unsent intent is indistinguishable from a
    // helmet that has not answered yet.
    //
    // `useLiveChannel`'s send is `if (readyState === OPEN) ws.send(...)` — a frame published while
    // the socket is down is dropped with no error, no queue and no return value, so `call_prepare`
    // would vanish and the panel would sit on "starting" for ever. The `channel_lost` guard below
    // does not cover this: it fires on the connected→disconnected TRANSITION while `liveRef` is
    // already set, and here the channel is down from the outset, so it never re-runs. The panel
    // self-starts on mount, so this is the whole gate.
    if (!connected) return fail('channel_lost')

    liveRef.current = true
    abortRef.current = new AbortController()
    setPhase({ kind: 'starting' })
    setMicOn(true)

    // Called straight from the click handler so the permission prompt is attributed to the gesture;
    // deferring it behind an await makes browsers refuse the prompt outright.
    // The REMEMBERED input, not the system default. The default is what produced a live, permitted,
    // completely silent microphone on a real emergency call; once an operator has picked the input
    // that works, every later call should start on it.
    void captureMic(readPreferredMic())
      .then(({ stream, usedPreferred }) => {
        // The remembered device is gone (unplugged, or a different machine). Forget it rather than
        // failing every future call against a device that no longer exists.
        if (!usedPreferred) writePreferredMic(null)
        if (!liveRef.current) {
          stream.getTracks().forEach(t => t.stop())

          return
        }

        micRef.current = stream

        // Everything downstream — the publish, the level meter, the silence detector — works on the
        // PROCESSED stream, so all three measure the signal the wearer actually receives.
        const chain = createMicChain(stream, gainRef.current)

        chainRef.current = chain
        // Surfaced as STATE, not just the ref, so the panel can measure the track and tell the
        // operator when their microphone is sending silence — a failure that is otherwise
        // invisible from this end and has to be discovered by asking the wearer.
        setMicStream(chain.stream)
        // Prepare only after the microphone is in hand: opening a call we then cannot speak on
        // would ring the wearer's helmet for nothing.
        preparedRef.current = true
        senderRef.current?.({ kind: 'call_prepare', viewerId: viewerIdRef.current })

        // Nothing else bounds the wait for `prepared`. The channel guard above proves the socket
        // was welcomed when we started, not that an ack ever comes back: a broker with no media
        // configuration drops the command with only a server-side warning, and answers nothing. The
        // phase then stays `starting` for ever — no dismiss button, because the panel offers one
        // only on a FINISHED call — with the microphone live and every LATER emergency blocked
        // behind a call the store still believes is running.
        timersRef.current.push(
          setTimeout(() => {
            if (liveRef.current && phaseRef.current.kind === 'starting') fail('no_broker_ack')
          }, PREPARE_ACK_MS),
        )
      })
      .catch((err: { name?: string; message?: string }) => {
        fail(err?.name === 'NotAllowedError' ? 'mic_denied' : 'mic_failed', err?.message)
      })
    // `connected` is in the deps on purpose: without it the closure keeps whatever the channel state
    // was when `start` was last rebuilt, and this callback's whole first act is to consult it.
  }, [robotId, origin, phase.kind, fail, connected])

  const hangUp = useCallback(() => {
    if (liveRef.current) senderRef.current?.({ kind: 'call_stop', viewerId: viewerIdRef.current })
    release()
    setPhase({ kind: 'ended', reason: 'hung_up' })
  }, [release])

  /**
   * Move the call to a different microphone, mid-sentence, without touching the call.
   *
   * `replaceTrack` on the existing sender: the peer connection, the SDP and the media server's view
   * of the stream are all unchanged, so the wearer hears the new input with no gap and no
   * re-handshake. Re-publishing instead would tear down the operator's stream and — while the old
   * one is still registered — collide with itself.
   */
  const switchMic = useCallback(async (deviceId: string): Promise<void> => {
    const sender = pushRef.current?.getSenders().find(x => x.track?.kind === 'audio')
    const { stream } = await captureMic(deviceId)
    // The new device gets its own gain stage, carrying the operator's setting across.
    const chain = createMicChain(stream, gainRef.current)
    const next = chain.stream.getAudioTracks()[0]

    if (!next) return

    // Carry the mute across: a switch must not turn a deliberately muted microphone back on.
    const wasOn = micRef.current?.getAudioTracks()[0]?.enabled ?? true

    next.enabled = wasOn
    stream.getAudioTracks().forEach(t => (t.enabled = wasOn))

    if (sender) await sender.replaceTrack(next)

    chainRef.current?.close()
    chainRef.current = chain

    // The OLD tracks last: stopping them first would leave the sender momentarily trackless, and
    // stopping them at all is what clears the operating system's microphone indicator for a device
    // the call is no longer using.
    micRef.current?.getTracks().forEach(t => t.stop())
    micRef.current = stream
    setMicStream(chain.stream)
    setMicOn(next.enabled)
    writePreferredMic(deviceId)
  }, [])

  /**
   * Change how loud the operator reaches the wearer, while they are talking.
   *
   * Live on the existing graph — no new capture, no `replaceTrack`, no renegotiation — so the
   * operator can slide it while speaking and hear the meter respond. Remembered per browser, because
   * a microphone that needed a boost once will need it on the next call too.
   */
  const setMicGain = useCallback((value: number) => {
    const next = clampGain(value)

    setMicGainState(next)
    writePreferredGain(next)
    chainRef.current?.setGain(next)
  }, [])

  const toggleMic = useCallback(() => {
    // Every track, RAW and processed: disabling only the raw one still lets the graph emit its
    // buffered tail, and disabling only the processed one leaves the operating system's microphone
    // indicator lit while the operator believes they are muted.
    const tracks = [...(micRef.current?.getAudioTracks() ?? []), ...(chainRef.current?.stream.getAudioTracks() ?? [])]
    const first = tracks[0]

    if (!first) return

    const next = !first.enabled

    tracks.forEach(t => (t.enabled = next))
    setMicOn(next)
  }, [])

  // Keep the broker's session alive while the media flows around it. Only the OPERATOR pings: a
  // helmet still polling after the operator's tab died must not hold its own call open.
  useEffect(() => {
    if (phase.kind !== 'ringing' && phase.kind !== 'connected') return

    const id = setInterval(() => senderRef.current?.({ kind: 'call_ping', viewerId: viewerIdRef.current }), PING_MS)

    return () => clearInterval(id)
  }, [phase.kind])

  // The command channel is how the call is steered AND ended. Losing it for GOOD means the helmet
  // can no longer be told anything, so the call is over whether we like it or not — saying so beats
  // showing a live-call indicator that is a lie.
  //
  // But only for good. `connected` drops on any ordinary websocket reconnect, and failing on the
  // first false killed healthy calls: the microphone was released mid-sentence and, because Accept
  // had already consumed the alarm, there was no way to dial back. The guard inside `start` stays
  // instant — there the channel is down BEFORE the call, which is a precondition, not a blip.
  useEffect(() => {
    if (connected || !liveRef.current) return

    const id = setTimeout(() => {
      if (liveRef.current) fail('channel_lost')
    }, CHANNEL_GRACE_MS)

    return () => clearTimeout(id)
  }, [connected, fail])

  return { phase, remoteStream, micOn, toggleMic, switchMic, micGain, setMicGain, start, hangUp, connected, micStream }
}
